import React, {useMemo} from "react";
import {PrimaryButton, type ICommandBarItemProps} from "@fluentui/react";
import {useTranslation} from "react-i18next";
import {useNavigate, useParams} from "react-router-dom";

import PathConstants from "../../../../routes/pathConstants";
import ConstructionStatus from "../../../../types/ConstructionStatus";
import CalculationStatus from "../../../../types/CalculationStatus";

import useSaveModifiedConstructionHook from "../../hooks/useSaveModifiedConstructionHook";
import useCalculationNewHook from "../../../../hooks/useCalculationNewHook";
import type ConstructionAllDetailsGuiDto from "../../../../dto/construction/ConstructionAllDetailsGuiDto";
import type CalculationResultDto from "../../../../dto/calculations/CalculationResultDto";

// Note: this does not take into account that foundations and footplates will also have wildcard
const isConstructionModified = (
  construction: ConstructionAllDetailsGuiDto | undefined,
  isAllowToTakeAction: boolean,
  selectedCalculationResults: CalculationResultDto,
): boolean => {
  if (construction) {
    const originalRequestHasMastWildCard = construction.constructionParts?.mastParts?.masts?.some(
      m =>
        m.profile?.value?.includes("?") ||
        (selectedCalculationResults?.editedConstructionProperties?.length ?? 0) > 0,
    );

    return isAllowToTakeAction && originalRequestHasMastWildCard;
  }
  return false;
};

export const useCommandBarItems = (
  construction: ConstructionAllDetailsGuiDto | undefined,
  isAllowToSave: boolean,
  isAllowedToChangeState: boolean,
  selectedCalculationResults: CalculationResultDto | undefined,
  hasErrors: boolean,
  handleConfirmModalOpenState: () => Promise<void>,
  handleOpenClosePanel: () => void,
): {
  commandBarItems: ICommandBarItemProps[];
  commandBarFarItems: ICommandBarItemProps[] | undefined;
} => {
  const navigate = useNavigate();
  const {id} = useParams();
  const {t} = useTranslation(["constructionCalculations"]);
  const options = useMemo(() => {
    if (!selectedCalculationResults) {
      return {
        showSaveModifiedBtn: false,
        canBeAccepted: false,
        canBeUnaccepted: false,
      };
    }

    const showSaveModifiedBtn =
      !hasErrors &&
      isConstructionModified(
        construction,
        isAllowToSave,
        selectedCalculationResults,
      ) &&
      construction?.status === ConstructionStatus.Registered &&
      selectedCalculationResults?.status === CalculationStatus.Completed;
    const canBeAccepted =
      !hasErrors &&
      isAllowedToChangeState &&
      selectedCalculationResults?.status === CalculationStatus.Completed &&
      construction?.status === ConstructionStatus.Registered;

    const canBeUnaccepted =
      !hasErrors &&
      isAllowedToChangeState &&
      selectedCalculationResults?.status === CalculationStatus.Completed &&
      construction?.status === ConstructionStatus.Accepted;

    return {showSaveModifiedBtn, canBeAccepted, canBeUnaccepted};
  }, [
    construction,
    hasErrors,
    isAllowToSave,
    isAllowedToChangeState,
    selectedCalculationResults,
  ]);

  const {
    downloadExcelInputMatrixById,
    downloadJsonInputMatrixById,
    downloadJsonCalculationConstructionById,
  } = useCalculationNewHook();
  const saveModifiedConstruction = useSaveModifiedConstructionHook();

  const handleSaveModifiedConstruction = async (): Promise<void> => {
    if (selectedCalculationResults?.calculationId && selectedCalculationResults?.endedOn){
      await saveModifiedConstruction(
        id,
        selectedCalculationResults.calculationId,
        selectedCalculationResults.endedOn,
      );
    }
  };

  const handleNavigateToCalculationsNote = (): void => {
    if(selectedCalculationResults?.calculationId){
      void navigate(
        `/${PathConstants.Constructions}/${id}/${PathConstants.Calculations}/${selectedCalculationResults?.calculationId}/${PathConstants.CalculationsNote}`,
        {state: {calculationId: selectedCalculationResults?.calculationId}},
      );
    }
  };

  const handleDownloadExcelInputMatrixTrigger = async (): Promise<void> => {
    if (selectedCalculationResults?.calculationId)
      await downloadExcelInputMatrixById(
        selectedCalculationResults.calculationId,
      );
  };

  const handleDownloadJsonInputMatrixTrigger = async (): Promise<void> => {
    if (selectedCalculationResults?.calculationId)
      await downloadJsonInputMatrixById(
        selectedCalculationResults.calculationId,
      );
  };

  const handleDownloadConstructionJsonTrigger = async (): Promise<void> => {
    if (selectedCalculationResults?.calculationId)
      await downloadJsonCalculationConstructionById(
        selectedCalculationResults.calculationId,
      );
  };

  const commandBarItems: ICommandBarItemProps[] = [
    {
      key: "calculations",
      onRender: () => (
        <PrimaryButton
          aria-label="View all calculations"
          text="View all calculations"
          iconProps={{iconName: "SidePanel"}}
          onClick={handleOpenClosePanel}
        />
      ),
    },
  ];

  if (!hasErrors) {
    commandBarItems.push({
      key: "calculationNote",
      onRender: () => (
        <PrimaryButton
          aria-label="Calculation note"
          text="Calculation note"
          iconProps={{iconName: "WordDocument"}}
          onClick={handleNavigateToCalculationsNote}
        />
      ),
    });
  }

  if (options.showSaveModifiedBtn) {
    commandBarItems.push({
      key: "saveModified",
      onRender: () => (
        <PrimaryButton
          aria-label="Save modified construction"
          text="Save modified construction"
          iconProps={{iconName: "SaveAs"}}
          // eslint-disable-next-line @typescript-eslint/no-misused-promises
          onClick={handleSaveModifiedConstruction}
        />
      ),
    });
  }

  if (options.canBeAccepted) {
    commandBarItems.push({
      key: "modifyAcceptedState",
      onRender: () => (
        <PrimaryButton
          aria-label={t(`btnAcceptAndSaveConstruction`)}
          text={t(`btnAcceptAndSaveConstruction`)}
          iconProps={{iconName: "Unlock"}}
          onClick={() => {
            void handleConfirmModalOpenState();
          }}
        />
      ),
    });
  }

  if (options.canBeUnaccepted) {
    commandBarItems.push({
      key: "modifyUnacceptedState",
      onRender: () => (
        <PrimaryButton
          aria-label={t(`btnUnacceptConstruction`)}
          text={t(`btnUnacceptConstruction`)}
          iconProps={{iconName: "Unlock"}}
          onClick={() => {
            void handleConfirmModalOpenState();
          }}
        />
      ),
    });
  }

  const commandBarFarItems = hasErrors
    ? undefined
    : [
        {
          key: "InputMatrix",
          onRender: () => (
            <PrimaryButton
              aria-label="Input Data"
              text="Input Data"
              iconProps={{
                iconName: "Download",
              }}
              menuProps={{
                items: [
                  {
                    key: "downloadExcelMatrix",
                    text: "Input Matrix.xlsx",
                    iconProps: {iconName: "ExcelDocument"},
                    onClick: () => {
                      void handleDownloadExcelInputMatrixTrigger();
                    },
                  },
                  {
                    key: "downloadJsonMatrix",
                    text: "Input Matrix.json",
                    iconProps: {iconName: "Embed"},
                    onClick: () => {
                      void handleDownloadJsonInputMatrixTrigger();
                    },
                  },
                  {
                    key: "downloadJsonConstruction",
                    text: "Construction.json",
                    iconProps: {iconName: "FileCode"},
                    onClick: () => {
                      void handleDownloadConstructionJsonTrigger();
                    },
                  },
                ],
              }}
            />
          ),
        },
      ];

  return {commandBarItems, commandBarFarItems};
};
